import React from 'react';
import { Box, Typography, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { 
  validatePasswordPolicy, 
  getPasswordStrength, 
  getPasswordStrengthLevel, 
  getPasswordStrengthColor, 
  getPasswordStrengthText,
  PasswordPolicyResult 
} from '../helpers/passwordPolicy';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
  showStrengthBar?: boolean;
  compact?: boolean;
  minPasswordLength?: number;
  requireSpecialChar?: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showRequirements = true,
  showStrengthBar = true,
  compact = false,
  minPasswordLength = 12,
  requireSpecialChar = true,
}) => {
  const policyResult: PasswordPolicyResult = validatePasswordPolicy(password, {
    minLength: minPasswordLength,
    requireSpecialChar: requireSpecialChar,
  });
  const strengthScore = getPasswordStrength(password);
  const strengthLevel = getPasswordStrengthLevel(strengthScore);
  const strengthColor = getPasswordStrengthColor(strengthLevel);
  const strengthText = getPasswordStrengthText(strengthLevel);

  if (!password) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      {showStrengthBar && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" sx={{ mr: 1 }}>
              ความแข็งแกร่งของรหัสผ่าน:
            </Typography>
            <Chip
              label={strengthText}
              size="small"
              sx={{
                backgroundColor: strengthColor,
                color: 'white',
                fontSize: '0.75rem',
                height: 20,
              }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={strengthScore}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: strengthColor,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}

      {showRequirements && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
            ข้อกำหนดรหัสผ่าน:
          </Typography>
          {compact ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                icon={policyResult.requirements.minLength ? <CheckCircle /> : <Cancel />}
                label={`อย่างน้อย ${minPasswordLength} ตัวอักษร`}
                size="small"
                variant="outlined"
                sx={{
                  color: policyResult.requirements.minLength ? '#4caf50' : '#f44336',
                  borderColor: policyResult.requirements.minLength ? '#4caf50' : '#f44336',
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                icon={policyResult.requirements.hasSpecialChar ? <CheckCircle /> : <Cancel />}
                label="มีอักขระพิเศษ"
                size="small"
                variant="outlined"
                sx={{
                  color: policyResult.requirements.hasSpecialChar ? '#4caf50' : '#f44336',
                  borderColor: policyResult.requirements.hasSpecialChar ? '#4caf50' : '#f44336',
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {policyResult.requirements.minLength ? (
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <Cancel sx={{ color: '#f44336', fontSize: 20 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`อย่างน้อย ${minPasswordLength} ตัวอักษร`}
                  primaryTypographyProps={{
                    variant: 'caption',
                    sx: { color: policyResult.requirements.minLength ? '#4caf50' : '#f44336' },
                  }}
                />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {policyResult.requirements.hasSpecialChar ? (
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <Cancel sx={{ color: '#f44336', fontSize: 20 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="มีอักขระพิเศษอย่างน้อย 1 ตัว"
                  primaryTypographyProps={{
                    variant: 'caption',
                    sx: { color: policyResult.requirements.hasSpecialChar ? '#4caf50' : '#f44336' },
                  }}
                />
              </ListItem>
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator;
